import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "conversation-list" }
const _hoisted_2 = {
  key: 0,
  class: "conversation-list__header"
}
const _hoisted_3 = { class: "conversation-list__items" }
const _hoisted_4 = {
  key: 0,
  class: "conversation-list__loading"
}
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "conversation-list__email-card-header" }
const _hoisted_7 = { class: "conversation-list__email-card-date" }
const _hoisted_8 = { class: "conversation-list__email-card-subject" }
const _hoisted_9 = { class: "conversation-list__email-card-sender" }
const _hoisted_10 = { class: "conversation-list__email-card-sender-info" }
const _hoisted_11 = { class: "conversation-list__email-card-sender-name" }
const _hoisted_12 = {
  key: 0,
  class: "conversation-list__email-card-sender-email"
}
const _hoisted_13 = {
  key: 0,
  class: "conversation-list__email-card-preview"
}
const _hoisted_14 = {
  key: 1,
  class: "conversation-list__email-card-thread"
}
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "conversation-list__avatar" }
const _hoisted_17 = ["src", "alt"]
const _hoisted_18 = { class: "conversation-list__item-body" }
const _hoisted_19 = { class: "conversation-list__item-header" }
const _hoisted_20 = { class: "conversation-list__item-title-wrapper" }
const _hoisted_21 = {
  key: 0,
  class: "conversation-list__item-separator"
}
const _hoisted_22 = {
  key: 1,
  class: "conversation-list__item-subtitle"
}
const _hoisted_23 = { class: "conversation-list__item-preview" }
const _hoisted_24 = { class: "conversation-list__item-badges" }
const _hoisted_25 = ["title"]
const _hoisted_26 = {
  key: 1,
  class: "conversation-list__item-badge"
}
const _hoisted_27 = {
  key: 3,
  class: "conversation-list__empty"
}

import { Input } from "@/components/ui";
import { Icon } from "@iconify/vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import { resolveLastMessagePreview } from "@/utils/messageFormatters";
import { ref } from "vue";

interface Conversation {
  id: string;
  title: string;
  subtitle?: string;
  subject?: string;
  lastMessage: string;
  lastMessageAt: Date | string;
  unreadCount?: number;
  isRead?: boolean;
  lastSeen?: string;
  threadCount?: number;
  contactEmail?: string;
  contactName?: string;
  displayName?: string;
  status?: string;
  lastMessageIsSender?: boolean;
  lastMessageSeen?: boolean;
  lastMessageDelivered?: boolean;
  lastMessageDirection?: string | null;
}

interface Props {
  conversations: Conversation[];
  selectedConversationId?: string;
  type?: "email" | "linkedin" | "whatsapp" | "sms";
  isLoading?: boolean;
  showSearch?: boolean;
  showHeader?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ConversationList',
  props: {
    conversations: { default: () => [] },
    selectedConversationId: { default: "" },
    type: { default: "email" },
    isLoading: { type: Boolean, default: false },
    showSearch: { type: Boolean, default: true },
    showHeader: { type: Boolean, default: true }
  },
  emits: ["conversation-selected"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const searchQuery = ref("");

const handleSelect = (conversation: Conversation) => {
  emit("conversation-selected", conversation);
};

const formatTime = (timestamp: Date | string): string => {
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp;
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor((startOfToday.getTime() - startOfDay.getTime()) / 86400000);

  if (diffDays === 0) {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  }
  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const getLastMessagePreview = (conversation: Conversation): string =>
  resolveLastMessagePreview(conversation);

const getConversationTitle = (conversation: any): string => {
  if (props.type === "email") {
    return (
      conversation.displayName ||
      conversation.title ||
      conversation.contactName ||
      conversation.contactEmail ||
      "Unknown"
    );
  }

  return conversation.title || conversation.contactName || "Unknown";
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const getAvatarColor = (conversation: any): string => {
  const name = getConversationTitle(conversation);
  const colors = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6", "#ef4444"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const isOutgoingMessage = (conversation: Conversation): boolean => {
  if (conversation.lastMessageIsSender) return true;
  if (conversation.lastMessageDirection === "outbound") return true;
  return false;
};

const getDeliveryIcon = (conversation: Conversation): string => {
  if (conversation.lastMessageSeen) return "mdi:check-all";
  if (conversation.lastMessageDelivered) return "mdi:check-all";
  return "mdi:check";
};

const getEmailStatus = (conversation: any): string => {
  return conversation.status || "sent";
};

const getStatusIcon = (conversation: any): string => {
  const status = getEmailStatus(conversation);
  switch (status) {
    case "read":
      return "mdi:check-all";
    case "delivered":
      return "mdi:check-all";
    case "sent":
    default:
      return "mdi:check";
  }
};

const getStatusLabel = (conversation: any): string => {
  const status = getEmailStatus(conversation);
  switch (status) {
    case "read":
      return "Read";
    case "delivered":
      return "Delivered";
    case "sent":
    default:
      return "Sent";
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.showHeader)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (__props.showSearch)
            ? (_openBlock(), _createBlock(_unref(Input), {
                key: 0,
                modelValue: searchQuery.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
                type: "search",
                placeholder: "Search conversations...",
                "leading-icon": "mdi:magnify",
                size: "sm",
                class: "conversation-list__search"
              }, null, 8, ["modelValue"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      (__props.isLoading && __props.conversations.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(LoadingBars, { size: "sm" })
          ]))
        : _createCommentVNode("", true),
      (__props.type === 'email')
        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(__props.conversations, (conversation) => {
            return (_openBlock(), _createElementBlock("div", {
              key: conversation.id,
              class: _normalizeClass(["conversation-list__email-card", {
            'conversation-list__email-card--selected': __props.selectedConversationId === conversation.id,
            'conversation-list__email-card--unread': !conversation.isRead,
          }]),
              onClick: ($event: any) => (handleSelect(conversation))
            }, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", {
                  class: _normalizeClass(["conversation-list__email-status-badge", `conversation-list__email-status-badge--${getEmailStatus(conversation)}`])
                }, [
                  _createVNode(_unref(Icon), {
                    icon: getStatusIcon(conversation),
                    class: "conversation-list__email-status-icon"
                  }, null, 8, ["icon"]),
                  _createElementVNode("span", null, _toDisplayString(getStatusLabel(conversation)), 1)
                ], 2),
                _createElementVNode("span", _hoisted_7, _toDisplayString(formatTime(conversation.lastMessageAt)), 1)
              ]),
              _createElementVNode("div", _hoisted_8, _toDisplayString(conversation.subject || conversation.lastMessage), 1),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", {
                  class: "conversation-list__email-card-avatar",
                  style: _normalizeStyle({ backgroundColor: getAvatarColor(conversation) })
                }, _toDisplayString(getInitials(getConversationTitle(conversation))), 5),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("span", _hoisted_11, _toDisplayString(getConversationTitle(conversation)), 1),
                  (conversation.contactEmail)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(conversation.contactEmail), 1))
                    : _createCommentVNode("", true)
                ])
              ]),
              (conversation.lastMessage && conversation.subject)
                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:reply",
                      class: "conversation-list__email-card-preview-icon"
                    }),
                    _createElementVNode("span", null, _toDisplayString(conversation.lastMessage), 1)
                  ]))
                : _createCommentVNode("", true),
              (conversation.threadCount && conversation.threadCount > 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:email-multiple-outline",
                      class: "conversation-list__email-card-thread-icon"
                    }),
                    _createElementVNode("span", null, "Thread · " + _toDisplayString(conversation.threadCount) + " messages", 1)
                  ]))
                : _createCommentVNode("", true)
            ], 10, _hoisted_5))
          }), 128))
        : (_openBlock(true), _createElementBlock(_Fragment, { key: 2 }, _renderList(__props.conversations, (conversation) => {
            return (_openBlock(), _createElementBlock("div", {
              key: conversation.id,
              class: _normalizeClass(["conversation-list__item", {
            'conversation-list__item--selected': __props.selectedConversationId === conversation.id,
            'conversation-list__item--unread': !conversation.isRead,
          }]),
              onClick: ($event: any) => (handleSelect(conversation))
            }, [
              _createElementVNode("div", _hoisted_16, [
                ((conversation as any).avatar)
                  ? (_openBlock(), _createElementBlock("img", {
                      key: 0,
                      src: (conversation as any).avatar,
                      alt: getConversationTitle(conversation),
                      class: "conversation-list__avatar-img",
                      onError: _cache[1] || (_cache[1] = ($event: any) => {
                ($event.target as HTMLImageElement).style.display = 'none';
                (
                  ($event.target as HTMLImageElement).nextElementSibling as HTMLElement
                ).style.display = 'flex';
              })
                    }, null, 40, _hoisted_17))
                  : _createCommentVNode("", true),
                _createElementVNode("span", {
                  class: _normalizeClass(["conversation-list__avatar-initials", { 'conversation-list__avatar-initials--whatsapp': __props.type === 'whatsapp' }]),
                  style: _normalizeStyle({ display: (conversation as any).avatar ? 'none' : 'flex' })
                }, _toDisplayString(getInitials(getConversationTitle(conversation))), 7)
              ]),
              _createElementVNode("div", _hoisted_18, [
                _createElementVNode("div", _hoisted_19, [
                  _createElementVNode("div", _hoisted_20, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["conversation-list__item-name", {
                    'conversation-list__item-name--unread':
                      conversation.unreadCount && conversation.unreadCount > 0,
                  }])
                    }, _toDisplayString(getConversationTitle(conversation)), 3),
                    (conversation.subtitle)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_21, "•"))
                      : _createCommentVNode("", true),
                    (conversation.subtitle)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(conversation.subtitle), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("span", {
                    class: _normalizeClass(["conversation-list__item-time", {
                  'conversation-list__item-time--unread':
                    conversation.unreadCount && conversation.unreadCount > 0,
                }])
                  }, _toDisplayString(formatTime(conversation.lastMessageAt)), 3)
                ]),
                _createElementVNode("div", _hoisted_23, [
                  (isOutgoingMessage(conversation))
                    ? (_openBlock(), _createBlock(_unref(Icon), {
                        key: 0,
                        icon: getDeliveryIcon(conversation),
                        class: _normalizeClass(["conversation-list__delivery-icon", { 'conversation-list__delivery-icon--read': conversation.lastMessageSeen }])
                      }, null, 8, ["icon", "class"]))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", {
                    class: _normalizeClass(["conversation-list__item-message", {
                  'conversation-list__item-message--unread':
                    conversation.unreadCount && conversation.unreadCount > 0,
                }])
                  }, _toDisplayString(getLastMessagePreview(conversation)), 3),
                  _createElementVNode("div", _hoisted_24, [
                    (conversation.threadCount && conversation.threadCount > 1)
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 0,
                          class: "conversation-list__item-thread-count",
                          title: `${conversation.threadCount} ${conversation.threadCount === 1 ? 'email' : 'emails'} in thread`
                        }, _toDisplayString(conversation.threadCount), 9, _hoisted_25))
                      : _createCommentVNode("", true),
                    (conversation.unreadCount && conversation.unreadCount > 0)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_26, _toDisplayString(conversation.unreadCount), 1))
                      : _createCommentVNode("", true)
                  ])
                ])
              ])
            ], 10, _hoisted_15))
          }), 128)),
      (!__props.isLoading && __props.conversations.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_27, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("p", null, "No conversations found", -1)
          ]))]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})